import React from 'react';
import { useNavigate } from 'react-router-dom';
import { infoPageStyles as styles } from './sharedStyles';
import { COLORS } from '../../constants';

const SellerResources = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.pageContainer}>
      <div style={styles.header}>
        <h1 style={styles.headerTitle}>Seller Resources</h1>
        <p style={styles.headerSubtitle}>
          Tips and best practices for successful selling
        </p>
      </div>

      <div style={styles.contentContainer}>
        {/* Overview */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Become a Successful Seller</h2>
          <p style={styles.text}>
            Whether you're selling textbooks, furniture, or electronics, these resources will help you 
            create compelling listings and complete successful transactions on Redbird Markets.
          </p>
        </div>

        {/* Creating Great Listings */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Creating Great Listings</h2>
          
          <h3 style={styles.subsectionTitle}>Photography Tips</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Use Natural Light:</strong> Take photos near windows or outdoors during daytime</li>
            <li style={styles.listItem}><strong>Clean Background:</strong> Use plain, uncluttered backgrounds</li>
            <li style={styles.listItem}><strong>Multiple Angles:</strong> Show front, back, sides, and any important details</li>
            <li style={styles.listItem}><strong>Show Flaws:</strong> Be honest and photograph any damage or wear</li>
            <li style={styles.listItem}><strong>Size Reference:</strong> Include common objects for scale when helpful</li>
            <li style={styles.listItem}><strong>High Quality:</strong> Use your best camera and avoid blurry photos</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Writing Descriptions</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Be Detailed:</strong> Include brand, model, size, color, and condition</li>
            <li style={styles.listItem}><strong>Be Honest:</strong> Mention any flaws, defects, or missing parts</li>
            <li style={styles.listItem}><strong>Include Measurements:</strong> Provide dimensions for furniture and clothing</li>
            <li style={styles.listItem}><strong>Tell a Story:</strong> Explain why you're selling and how you used it</li>
            <li style={styles.listItem}><strong>Use Keywords:</strong> Include searchable terms buyers might use</li>
            <li style={styles.listItem}><strong>Proofread:</strong> Check for spelling and grammar errors</li>
          </ul>
        </div>

        {/* Pricing Strategy */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Pricing Strategy</h2>
          
          <div style={styles.highlight}>
            <h3 style={styles.highlightTitle}>Pricing Tips</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Research similar items on Redbird Markets and other platforms</li>
              <li style={styles.listItem}>Consider original price, age, and condition</li>
              <li style={styles.listItem}>Be realistic about used item values</li>
            </ul>
          </div>

          <h3 style={styles.subsectionTitle}>Common Pricing Guidelines</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Like New:</strong> 70-90% of original price</li>
            <li style={styles.listItem}><strong>Good Condition:</strong> 50-70% of original price</li>
            <li style={styles.listItem}><strong>Fair Condition:</strong> 30-50% of original price</li>
            <li style={styles.listItem}><strong>Textbooks:</strong> 40-60% of retail (depending on edition and condition)</li>
          </ul>
        </div>

        {/* Communication Best Practices */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Communication Best Practices</h2>
          
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Respond Quickly:</strong> Reply to inquiries within 24 hours</li>
            <li style={styles.listItem}><strong>Be Professional:</strong> Use polite, clear language</li>
            <li style={styles.listItem}><strong>Answer Questions:</strong> Provide detailed responses to buyer questions</li>
            <li style={styles.listItem}><strong>Be Available:</strong> Offer flexible meeting times</li>
            <li style={styles.listItem}><strong>Confirm Details:</strong> Double-check meeting times and locations</li>
            <li style={styles.listItem}><strong>Follow Through:</strong> Honor your commitments</li>
          </ul>
        </div>

        {/* Managing Transactions */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Managing Transactions</h2>
          
          <h3 style={styles.subsectionTitle}>Meeting Buyers</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Suggest safe, public locations on campus</li>
            <li style={styles.listItem}>Be punctual and notify if you'll be late</li>
            <li style={styles.listItem}>Bring the item clean and ready to inspect</li>
            <li style={styles.listItem}>Be prepared to answer questions about the item</li>
          </ul>

          <h3 style={styles.subsectionTitle}>After the Sale</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}>Thank the buyer</li>
            <li style={styles.listItem}>Keep transaction records for your reference</li>
            <li style={styles.listItem}>Mark item as sold when transaction completes</li>
            <li style={styles.listItem}>Use "Contact Buyer" for any post-sale communication</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Managing Your Listing History</h3>
          <div style={styles.highlight}>
            <h4 style={styles.highlightTitle}>Archive System</h4>
            <p style={styles.text}>
              Keep your dashboard organized with our smart archive system:
            </p>
            <ul style={styles.list}>
              <li style={styles.listItem}><strong>Archive completed sales:</strong> Hide sold items from your main dashboard while preserving transaction history</li>
              <li style={styles.listItem}><strong>Automatic cleanup:</strong> Items archived for 90+ days are moved to deep storage automatically</li>
              <li style={styles.listItem}><strong>Transaction preservation:</strong> Your sales records and buyer contact info are always kept safe</li>
              <li style={styles.listItem}><strong>Easy access:</strong> View archived items anytime from your listings page</li>
            </ul>
          </div>
        </div>

        {/* Payment Information */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>How Payments Work</h2>
          
          <div style={styles.highlight}>
            <h3 style={styles.highlightTitle}>Automatic Payments</h3>
            <p style={styles.text}>
              <strong>You don't need to withdraw money manually!</strong> When someone buys your item, 
              Stripe automatically deposits the money directly into your bank account within 1-2 business days.
            </p>
          </div>

          <h3 style={styles.subsectionTitle}>Payment Schedule</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Daily Deposits:</strong> Stripe processes payouts daily to your bank account</li>
            <li style={styles.listItem}><strong>No Manual Action:</strong> Completely automatic - no need to log in and "withdraw"</li>
            <li style={styles.listItem}><strong>Bank Direct:</strong> Money goes straight to the bank account you provide during setup</li>
            <li style={styles.listItem}><strong>Fast Processing:</strong> Usually arrives within 1-2 business days</li>
          </ul>

          <h3 style={styles.subsectionTitle}>Seller Fees</h3>
          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Platform Fee:</strong> 5% of sale price (supports the marketplace)</li>
            <li style={styles.listItem}><strong>Processing Fee:</strong> Stripe charges 2.9% + $0.30 (industry standard)</li>
            <li style={styles.listItem}><strong>Automatic Deduction:</strong> Fees are subtracted before deposit</li>
            <li style={styles.listItem}><strong>No Hidden Costs:</strong> No monthly fees or surprise charges</li>
          </ul>

          <div style={styles.highlight}>
            <h4 style={styles.highlightTitle}>Example: You sell a $100 textbook</h4>
            <p style={styles.text}>
              • Buyer pays: $100.00<br/>
              • Platform fee: -$5.00 (5%)<br/>
              • Stripe fee: -$3.20 (2.9% + $0.30)<br/>
              • <strong>You receive: $91.80 deposited automatically</strong>
            </p>
          </div>
        </div>

        {/* Seller Dashboard */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Using Your Seller Dashboard</h2>
          
          <p style={styles.text}>
            Access your Seller Dashboard to manage all your selling activities in one place:
          </p>

          <ul style={styles.list}>
            <li style={styles.listItem}><strong>Active Listings:</strong> View and edit your current listings</li>
            <li style={styles.listItem}><strong>Sales History:</strong> Track completed transactions</li>
            <li style={styles.listItem}><strong>Earnings:</strong> Monitor your total earnings and automatic deposits</li>
            <li style={styles.listItem}><strong>Messages:</strong> Respond to buyer inquiries</li>
          </ul>

          <button
            style={styles.button}
            onClick={() => navigate('/seller-dashboard')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.primary}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary}30`;
            }}
          >
            Go to Seller Dashboard
          </button>
        </div>

        {/* Dos and Don'ts */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Seller Do's and Don'ts</h2>
          
          <div style={styles.highlight}>
            <h3 style={styles.highlightTitle}>DO:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Be honest about item condition</li>
              <li style={styles.listItem}>Respond to messages promptly</li>
              <li style={styles.listItem}>Update listings with price changes</li>
              <li style={styles.listItem}>Meet in safe, public locations</li>
              <li style={styles.listItem}>Respect buyers' time and questions</li>
            </ul>
          </div>

          <div style={{...styles.highlight, background: `${COLORS.primary}08`, borderColor: '#d61c2e'}}>
            <h3 style={{...styles.highlightTitle, color: '#d61c2e'}}>DON'T:</h3>
            <ul style={styles.list}>
              <li style={styles.listItem}>Misrepresent item condition or features</li>
              <li style={styles.listItem}>Ghost buyers after agreeing to meet</li>
              <li style={styles.listItem}>Refuse reasonable requests for more photos</li>
              <li style={styles.listItem}>Engage in price gouging or unfair practices</li>
              <li style={styles.listItem}>Sell prohibited or illegal items</li>
            </ul>
          </div>
        </div>

        {/* Start Selling CTA */}
        <div style={{...styles.section, textAlign: 'center'}}>
          <h2 style={styles.sectionTitle}>Ready to Start Selling?</h2>
          <p style={styles.text}>
            Create your first listing and join thousands of successful sellers on Redbird Markets.
          </p>
          <button
            style={styles.button}
            onClick={() => navigate('/add-listing')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = `0 6px 20px ${COLORS.primary}40`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = `0 4px 12px ${COLORS.primary}30`;
            }}
          >
            Create a Listing
          </button>
        </div>
      </div>
    </div>
  );
};

export default SellerResources;
